/**
 * Capital return velocity — THE flagship card (founder 2026-07-13: "our
 * cornerstone; has to be done very well and prominent"). ONE component, rendered
 * identically on the Exec cockpit and Financials (two placements, one approval).
 *
 * It is the CASH journey: inventory time before the sale, then the cumulative
 * Contribution Margin line clearing acquisition and overhead separately, then
 * payout lag. Product cost is already inside Contribution Margin, so inventory
 * is time, never a second dollar hurdle.
 *
 * It ABSORBS the old Exec payback curve AND the Financials "Your cash cycle &
 * payback" card (the dollar-days notes are its footer). Everything rides the ONE
 * shared payback basis (server: /dashboard/profit-model + /dashboard/ltv-payback,
 * same window) — the line and the cost bar can never tell two stories, and the
 * break-even % is the ONE shared derivation the equation strip reuses.
 *
 * The compact CRV number tile in the cockpit is this card's sibling; clicking it
 * scrolls here.
 */

import { useQuery } from "@tanstack/react-query";
import { useApiClient } from "../lib/api-context.js";
import { usePeriod } from "../lib/period.js";
import { CardAnchorCtx } from "../lib/card-anchor.js";
import { Card, CardHead, moneyFmt } from "./cards.js";
import { CmGapNotice } from "./CmGapNotice.js";
import { LeverNeedWarning } from "./LeverNeedWarning.js";
import { PaybackCurveChart, paybackCostPct, paybackLayerDays, type PaybackData } from "./PaybackCurveChart.js";
import type { CmGap } from "../lib/card-requirements.js";
import { DataTrustCardNotice } from "../lib/data-trust-banner.js";
import { inventoryTimingLabel } from "../lib/cash-timing-format.js";

interface LeverField { value: number | null; prior: number | null; status: "live" | "needs_setup"; needs?: string; sample?: number }
interface Pm {
  periodLabel: string;
  capabilities: { contributionMargin: boolean; cmGap?: CmGap | null };
  capitalReturnVelocity: {
    months: LeverField;
    inventoryDays: number | null;
    settlementDays: number | null;
    paybackDays: number | null;
    totalInventoryDays: number | null;
    totalCrvMonths: number | null;
    financing: { financedPct: number; ratePct: number } | null;
    inventoryCoveragePct: number;
    missingInventoryTimingSkus: number;
  };
  paybackBreakEvenDay: number | null;
  paybackUnmeasurableReason: "needs_full_month" | "before_spend_coverage" | null;
  cashCycle: { dailyCogs: number | null; floatDays: number | null; cashInFloat: number | null; financedInFloat: number | null };
}

const monthsFmt = (v: number | null | undefined): string => (v === null || v === undefined ? "—" : `${v.toFixed(1)} mo`);

function cmGapOf(pm: Pm): CmGap | null {
  return pm.capabilities.cmGap && pm.capabilities.cmGap.missingProducts > 0 ? pm.capabilities.cmGap : null;
}

/** The flagship. `anchor` = the placement renderId ("pm-payback-curve" on Exec,
 *  "fin-payback-layers" on Financials) — sets the See-it-live/scroll target and
 *  lets the header resolve an operator rename. */
export function CapitalReturnVelocityCard({ anchor }: { anchor: string }): React.ReactElement {
  const api = useApiClient();
  const period = usePeriod();

  const pmQ = useQuery({
    queryKey: ["profit-model", period.cacheKey],
    queryFn: () => api.get<Pm>("/dashboard/profit-model", { ...period.query }),
  });
  const paybackQ = useQuery({
    queryKey: ["fin-payback", period.cacheKey],
    queryFn: () => api.get<PaybackData>("/dashboard/ltv-payback", { ...period.query }),
  });

  const pm = pmQ.data;
  const payback = paybackQ.data;
  const cmGap = pm ? cmGapOf(pm) : null;

  const explain = [
    "<b>What it is:</b> How long a dollar stays out of your bank account before it comes back as usable cash. It follows four stages: inventory, acquisition, overhead, and payout.",
    "<b>How it's calculated:</b> Inventory is the time your own cash is tied up before the sale. Products that tie up more product-cost dollars carry more weight in that inventory time. The line then tracks Contribution Margin from the most recent fully completed customer group. It first covers what that group cost to acquire, then its share of the overhead recorded during those same acquisition dates. Payout lag is added last. Product cost is already removed inside Contribution Margin, so inventory is counted as time and never charged twice.",
    "<b>Example (sample data):</b> 26 days in inventory + 1 day to recover acquisition + 36 more days to recover overhead + 6 days for payout = 69 days, or 2.3 months.",
    "<b>Note:</b> Blended. Acquisition and overhead stay separate because they are different levers. The overhead layer gives each active buyer one share of that month's overhead. It does not charge them again just because time passes. If overhead is unconfirmed or a month needs the planning fallback, the card tells you. If the group has not paid back, it shows progress instead of making up a date.",
  ];

  return (
    <CardAnchorCtx.Provider value={anchor}>
      <Card anchor={anchor}>
        <CardHead title={`Capital return velocity · ${pm?.periodLabel ?? period.label}`} explain={explain} />
        <DataTrustCardNotice card="Capital Return Velocity" />

        {!pm ? (
          <p style={{ color: "var(--body)", fontSize: 13, marginTop: 10 }}>{pmQ.isError ? "Couldn't load the money story. Refresh to retry." : "Working out the round trip…"}</p>
        ) : cmGap ? (
          <div style={{ marginTop: 10 }}><CmGapNotice gap={cmGap} /></div>
        ) : (
          <>
            <Headline pm={pm} payback={payback} />
            <div style={{ marginTop: 12 }}>
              {!pm.capabilities.contributionMargin ? (
                <div style={notice}>Add your product costs to see this. The payback journey is built from Contribution Margin, so there is nothing to measure until costs are entered.</div>
              ) : pm.paybackUnmeasurableReason ? (
                <div style={notice}>
                  {pm.paybackUnmeasurableReason === "needs_full_month"
                    ? "This view needs at least 1 full month of history. Widen the timeline."
                    : "Acquisition costs aren't tracked in this window. Widen the timeline into your covered history."}
                </div>
              ) : payback ? (
                <>
                  <PaybackCurveChart data={payback} inventoryDays={pm.capitalReturnVelocity.inventoryDays} settlementDays={pm.capitalReturnVelocity.settlementDays} />
                  <NextMove payback={payback} inventoryDays={pm.capitalReturnVelocity.inventoryDays} settlementDays={pm.capitalReturnVelocity.settlementDays} />
                </>
              ) : paybackQ.isError ? (
                <p style={{ color: "var(--bad)", fontSize: 13 }}>Couldn't load the curve. Refresh to retry.</p>
              ) : (
                <p style={{ color: "var(--body)", fontSize: 13 }}>Loading the journey…</p>
              )}
            </div>
            <CashNotes pm={pm} />
          </>
        )}
      </Card>
    </CardAnchorCtx.Provider>
  );
}

/** The CRV-months headline + the prominent "% of the way to full break-even"
 *  progress line (the founder's favourite) whenever this window's customers
 *  haven't crossed the full stack for the selected basis. */
function Headline({ pm, payback }: { pm: Pm; payback: PaybackData | undefined }): React.ReactElement {
  const field = pm.capitalReturnVelocity.months;
  const live = field.status === "live" && field.value !== null;
  const needsSetup = field.status === "needs_setup";

  // The ONE shared break-even %: last measured cumulative margin ÷ acquisition
  // plus overhead (packages/shared, via paybackCostPct). Shown prominently
  // while the line is measurable but hasn't crossed the full stack.
  const pct = payback ? paybackCostPct(payback) : null;
  const crossed = pm.paybackBreakEvenDay !== null;
  const showPct = pct !== null && pct < 1 && !crossed && pm.capabilities.contributionMargin && !pm.paybackUnmeasurableReason;

  const crv = pm.capitalReturnVelocity;
  const liveSub = live && crv.inventoryDays !== null && crv.paybackDays !== null
    ? `${inventoryTimingLabel(crv.inventoryDays)} + ${Math.round(crv.paybackDays)}d to recover acquisition and overhead${(crv.settlementDays ?? 0) >= 1 ? ` + ${Math.round(crv.settlementDays!)}d payout lag` : ""}`
    : live ? "" : "No full round trip inside this window yet. The progress below shows how far along you are.";

  if (needsSetup) {
    // No-pretend-numbers ruling (founder QA 2026-07-15): the flagship's headline shows
    // the amber "needs setup" warning, NEVER a "6.0 mo (sample)" behind a SAMPLE badge.
    return (
      <div style={{ marginTop: 8 }}>
        <LeverNeedWarning needs={field.needs} compact />
        {field.needs === "inventory" && pm.capitalReturnVelocity.missingInventoryTimingSkus > 0 && (
          <div style={{ marginTop: 6, fontSize: 11.5, color: "var(--text-faint)", lineHeight: 1.45 }}>
            Supplier timing is missing for {pm.capitalReturnVelocity.missingInventoryTimingSkus.toLocaleString()} sold physical product{pm.capitalReturnVelocity.missingInventoryTimingSkus === 1 ? "" : "s"}. The card stays blank instead of guessing the missing cash time.
          </div>
        )}
      </div>
    );
  }
  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
        <span className="dd-num" style={{ fontSize: 30, fontWeight: 800, color: "var(--num)" }}>{monthsFmt(field.value)}</span>
        <span style={{ fontSize: 12, color: "var(--body)" }}>to bring a dollar back as cash</span>
      </div>
      {liveSub && <div style={{ fontSize: 11.5, color: "var(--text-faint)", marginTop: 3 }}>{liveSub}</div>}
      {showPct && (
        <div style={{ marginTop: 9, padding: "9px 12px", borderRadius: 8, background: "color-mix(in srgb, var(--accent) 8%, transparent)", border: "1px solid color-mix(in srgb, var(--accent) 30%, var(--border))", display: "flex", alignItems: "baseline", gap: 8 }}>
          <span className="dd-num" style={{ fontSize: 22, fontWeight: 800, color: "var(--accent)" }}>{Math.round(pct! * 100)}%</span>
          <span style={{ fontSize: 12.5, color: "var(--body)" }}>of acquisition and overhead recovered so far. Inventory and payout time are added once this customer group reaches full payback.</span>
        </div>
      )}
    </div>
  );
}

/** One directive under the curve based on the longest measured time stage. */
function NextMove({ payback, inventoryDays, settlementDays }: { payback: PaybackData; inventoryDays: number | null; settlementDays: number | null }): React.ReactElement | null {
  if (!payback.hasCm) return null;
  const covered = paybackLayerDays(payback);
  if (inventoryDays === null || covered.acquisition === null || covered.overhead === null) return null;
  const stages = {
    inventory: Math.max(0, inventoryDays),
    acquisition: Math.max(0, covered.acquisition),
    overhead: Math.max(0, covered.overhead - covered.acquisition),
    payout: Math.max(0, settlementDays ?? 0),
  };
  const biggest = (Object.entries(stages) as Array<[keyof typeof stages, number]>).reduce((best, current) => current[1] > best[1] ? current : best)[0];
  if (stages[biggest] < 1) return null;
  const text =
    biggest === "inventory" ? (
      <>the longest delay is <b>inventory</b> ({Math.round(stages.inventory)} days). Shorter lead times, better supplier terms, or smaller orders would free cash sooner.</>
    ) : biggest === "acquisition" ? (
      <>the longest delay is <b>recovering acquisition</b> ({Math.round(stages.acquisition)} days). More Contribution Margin on the first order or a lower customer acquisition cost would shorten it.</>
    ) : biggest === "overhead" ? (
      <>the longest delay is <b>recovering overhead</b> ({Math.round(stages.overhead)} days). More Contribution Margin per customer, more buyers on the same fixed costs, or lower overhead would shorten it.</>
    ) : (
      <>the longest delay is <b>waiting for payouts</b> ({Math.round(stages.payout)} days). Faster processor terms or a smaller reserve would bring cash back sooner.</>
    );
  return (
    <div style={nextMove}>
      <span style={{ fontWeight: 700, color: "var(--accent)" }}>Your next move:</span> {text}
    </div>
  );
}

/** The dollar-days footer the founder likes (settlement lag, cash parked in
 *  stock, financing, the dollar-days rule) — carried over from the absorbed
 *  cash-cycle card so nothing of its value is lost. */
function CashNotes({ pm }: { pm: Pm }): React.ReactElement | null {
  const crv = pm.capitalReturnVelocity;
  const { dailyCogs, cashInFloat, financedInFloat, floatDays } = pm.cashCycle;
  const financing = crv.financing;
  const financeDaysSaved = crv.totalInventoryDays !== null && crv.inventoryDays !== null
    ? Math.max(0, crv.totalInventoryDays - crv.inventoryDays)
    : null;
  const items: React.ReactNode[] = [];
  if ((crv.settlementDays ?? 0) >= 1) items.push(<span key="settle">Includes <b className="dd-num" style={{ color: "var(--num)" }}>{Math.round(crv.settlementDays!)}d</b> waiting on processor payouts, including any reserve</span>);
  if (cashInFloat !== null) {
    items.push(cashInFloat < 0
      ? <span key="parked">Supplier terms are funding about <b className="dd-num" style={{ color: "var(--good)" }}>{moneyFmt(Math.abs(cashInFloat))}</b> of working capital</span>
      : <span key="parked">Cash parked in stock right now ≈ <b className="dd-num" style={{ color: "var(--num)" }}>{moneyFmt(cashInFloat)}</b></span>);
  }
  if (financing !== null && financedInFloat !== null && financedInFloat > 0) {
    items.push(
      <span key="fin">Your credit line (<b className="dd-num">{Math.round(financing.financedPct)}%</b> of stock at <b className="dd-num">{financing.ratePct}%</b> APR) frees
        {financeDaysSaved !== null && financeDaysSaved >= 1 && <> about <b className="dd-num" style={{ color: "var(--good)" }}>{Math.round(financeDaysSaved)}d</b> of</>} your own cash. The interest is already in your margins. Your estimated credit-line balance is <b className="dd-num" style={{ color: "var(--num)" }}>{moneyFmt(financedInFloat)}</b>.</span>,
    );
  }
  if (dailyCogs !== null && floatDays !== null) items.push(floatDays < 0
    ? <span key="dd">Each extra day before the supplier is due improves working capital by about <b className="dd-num" style={{ color: "var(--good)" }}>{moneyFmt(dailyCogs)}</b></span>
    : <span key="dd">Every day cut from the <b>stock float</b> frees about <b className="dd-num" style={{ color: "var(--good)" }}>{moneyFmt(dailyCogs)}</b></span>);
  if (items.length === 0) return null;
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 22px", marginTop: 10, fontSize: 12.5, color: "var(--body)" }}>{items}</div>
  );
}

const notice: React.CSSProperties = {
  padding: "20px 18px", background: "var(--surface-2)", border: "1px dashed var(--border)",
  borderRadius: "var(--r-md)", color: "var(--body)", fontSize: 13, lineHeight: 1.55,
};
const nextMove: React.CSSProperties = {
  marginTop: 12, padding: "10px 13px", fontSize: 12.5, lineHeight: 1.5, color: "var(--body)",
  background: "color-mix(in srgb, var(--accent) 7%, transparent)",
  border: "1px solid color-mix(in srgb, var(--accent) 30%, var(--border))",
  borderRadius: "var(--r-sm)",
};
